import { config } from "@/app.config"
import { ResourceType } from "@/models"

import { EndpointTypes } from "./endpoint.types"

export const BASE_URL = "/api/v1"

class Url {
  public url: string
  public logUrl: string
  public externalUrl: string
  public logsError: boolean

  constructor(url, logUrl?: string, logsError = true) {
    this.url = url
    this.logUrl = logUrl || url
    this.externalUrl = `${config.VUE_APP_FILES_LOCATION}${BASE_URL}${url}`
    this.logsError = logsError
  }
}

export class Endpoint extends Url {
  // login
  static readonly [EndpointTypes.LOGIN] = () => new Url("/on-login", "/on-login")

  // chat-gpt
  static readonly [EndpointTypes.CHAT_GPT_ASK] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/ai-db-assistant/ask`,
      "/account/${accountId}/v2/ai-db-assistant/ask"
    )
  static readonly [EndpointTypes.CHAT_GPT_RESET] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/ai-db-assistant/reset`,
      "/account/${accountId}/v1/ai-db-assistant/reset"
    )
  static readonly [EndpointTypes.SPRINT_ANALYTICS_INSIGHTS] = (
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/analytics/insights`,
      "/project/${projectId}/v1/sprint/${sprintId}/analytics/insights"
    )
  static readonly [EndpointTypes.GET_AI_CONFIG] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-config/providers`,
      "/account/${accountId}/v1/admin/ai-config/providers"
    )
  static readonly [EndpointTypes.GET_AI_MODELS] = () =>
    new Url("/superadmin/llm-models", "/superadmin/llm-models")
  static readonly [EndpointTypes.GET_AI_MODEL_PROVIDERS] = () =>
    new Url("/superadmin/llm-models/providers", "/superadmin/llm-models/providers")
  static readonly [EndpointTypes.CREATE_AI_MODEL] = () =>
    new Url("/superadmin/llm-models", "/superadmin/llm-models")
  static readonly [EndpointTypes.DELETE_AI_MODEL] = (modelId: string) =>
    new Url(`/superadmin/llm-models/${modelId}`, "/superadmin/llm-models/${modelId}")
  static readonly [EndpointTypes.UPDATE_AI_MODEL_STATUS] = (modelId: string) =>
    new Url(`/superadmin/llm-models/${modelId}/status`, "/superadmin/llm-models/${modelId}/status")
  static readonly [EndpointTypes.HAS_AI_CONFIG] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-config/hasConfig`,
      "/account/${accountId}/v1/admin/ai-config/hasConfig"
    )
  static readonly [EndpointTypes.AI_CONFIG_UPDATE] = (accountId: string, aiConfigId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-config/${aiConfigId}`,
      "/account/${accountId}/v1/admin/ai-config/${aiConfigId}"
    )
  static readonly [EndpointTypes.AI_CONFIG_CREATE] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/admin/ai-config`, "/account/${accountId}/v1/admin/ai-config")
  static readonly [EndpointTypes.AI_CONFIG_TOKEN_CHECK] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-config/token/validation/check/`,
      "/account/${accountId}/v1/admin/ai-config/token/validation/check/"
    )
  static readonly [EndpointTypes.AI_CONFIG] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/admin/ai-config`, "/account/${accountId}/v1/admin/ai-config")
  static readonly [EndpointTypes.AI_CONFIG_PROFILE] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-config/profile`,
      "/account/${accountId}/v1/admin/ai-config/profile"
    )
  static readonly [EndpointTypes.AI_CONFIG_BY_ID] = (accountId: string, aiConfigId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-config/${aiConfigId}`,
      "/account/${accountId}/v1/admin/ai-config/${aiConfigId}"
    )
  static readonly [EndpointTypes.GET_AI_PROMPTS] = (projectId: string) =>
    new Url(`/project/${projectId}/ai-prompts`, "/project/${projectId}/ai-prompts")
  static readonly [EndpointTypes.AI_CONVERSATION_GET_OR_CREATE] = (accountId: string) =>
    new Url(
      `/account/${accountId}/ai-conversations/get-or-create`,
      "/account/${accountId}/ai-conversations/get-or-create"
    )
  static readonly [EndpointTypes.AI_CONVERSATION] = (accountId: string, conversationId: string) =>
    new Url(
      `/account/${accountId}/ai-conversations/${conversationId}`,
      "/account/${accountId}/ai-conversations/${conversationId}"
    )
  static readonly [EndpointTypes.AI_CONVERSATION_MESSAGES] = (
    accountId: string,
    conversationId: string
  ) =>
    new Url(
      `/account/${accountId}/ai-conversations/${conversationId}/messages`,
      "/account/${accountId}/ai-conversations/${conversationId}/messages"
    )
  static readonly [EndpointTypes.AI_CONVERSATION_MESSAGES_DELETE] = (
    accountId: string,
    conversationId: string
  ) =>
    new Url(
      `/account/${accountId}/ai-conversations/${conversationId}/messages`,
      "/account/${accountId}/ai-conversations/${conversationId}/messages"
    )
  static readonly [EndpointTypes.AI_CONVERSATION_CLEAR_CONTEXT] = (
    accountId: string,
    conversationId: string
  ) =>
    new Url(
      `/account/${accountId}/ai-conversations/${conversationId}/clear-context`,
      "/account/${accountId}/ai-conversations/${conversationId}/clear-context"
    )
  static readonly [EndpointTypes.AI_PROMPT] = (projectId: string, promptId: string) =>
    new Url(
      `/project/${projectId}/ai-prompts/${promptId}`,
      "/project/${projectId}/ai-prompts/${promptId}"
    )
  static readonly [EndpointTypes.AI_PROMPT_ENABLED] = (projectId: string, promptId: string) =>
    new Url(
      `/project/${projectId}/ai-prompts/${promptId}/enabled`,
      "/project/${projectId}/ai-prompts/${promptId}/enabled"
    )
  static readonly [EndpointTypes.AI_PROMPT_RUN] = (
    projectId: string,
    promptId: string,
    accountId: string
  ) =>
    new Url(
      `/project/${projectId}/ai-prompts/${promptId}/account/${accountId}/run/override`,
      `/project/${projectId}/ai-prompts/${promptId}/account/${accountId}/run/override`
    )
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_ACCOUNT] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-requests`,
      "/account/${accountId}/v1/admin/ai-requests"
    )
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_ACCOUNT_DETAIL] = (
    accountId: string,
    requestId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-requests/${requestId}`,
      "/account/${accountId}/v1/admin/ai-requests/${requestId}"
    )
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_ACCOUNT_STATS] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/ai-requests/stats`,
      "/account/${accountId}/v1/admin/ai-requests/stats"
    )
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_ACCOUNT_SEARCH] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/project/search-items`,
      "/account/${accountId}/v1/admin/project/search-items"
    )
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_SUPERADMIN] = () =>
    new Url("/superadmin/ai-requests", "/superadmin/ai-requests")
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_SUPERADMIN_DETAIL] = (requestId: string) =>
    new Url(`/superadmin/ai-requests/${requestId}`, "/superadmin/ai-requests/${requestId}")
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_SUPERADMIN_STATS] = () =>
    new Url("/superadmin/ai-requests/stats", "/superadmin/ai-requests/stats")
  static readonly [EndpointTypes.AI_ACTIVITY_LOG_SUPERADMIN_SEARCH] = () =>
    new Url("/superadmin/project/search", "/superadmin/project/search")
  static readonly [EndpointTypes.ITEM_AI_ACCEPTANCE_CRITERIA] = (
    accountId: string,
    projectId: string
  ) =>
    new Url(
      `/account/${accountId}/project/${projectId}/item/ai/acceptanceCriteria`,
      "/account/${accountId}/project/${projectId}/item/ai/acceptanceCriteria"
    )
  static readonly [EndpointTypes.ITEM_AI_ACCEPTANCE_CRITERIA_REGENERATE] = (
    accountId: string,
    projectId: string
  ) =>
    new Url(
      `/account/${accountId}/project/${projectId}/item/ai/acceptanceCriteria/regenerate`,
      "/account/${accountId}/project/${projectId}/item/ai/acceptanceCriteria/regenerate"
    )

  // account
  static readonly [EndpointTypes.GET_ACCOUNTS] = () => new Url("admin/account", "admin/account")
  static readonly [EndpointTypes.GET_ACCOUNTS_ANALYTICS] = () =>
    new Url("superadmin/account/analytics", "superadmin/account/analytics")
  static readonly [EndpointTypes.GET_DEMO_REQUESTS] = () =>
    new Url("superadmin/demoRequest/search", "superadmin/demoRequest/search")
  static readonly [EndpointTypes.DELETE_DEMO_REQUESTS] = demoRequestId =>
    new Url(`superadmin/demoRequest/${demoRequestId}`, "superadmin/demoRequest/${demoRequestId}")
  static readonly [EndpointTypes.GET_ACCOUNT] = (accountId: string) =>
    new Url(`/account/extended/${accountId}`, "/account/extended/${accountId}")
  static readonly [EndpointTypes.UPDATE_ACCOUNT] = (accountId: string) =>
    new Url(`/account/${accountId}`, "/account/${accountId}")
  static readonly [EndpointTypes.GET_ACCOUNT_ADMIN] = (accountId: string) =>
    new Url(`admin/account/${accountId}`, "admin/account/${accountId}")
  static readonly [EndpointTypes.GET_ACCOUNT_SUPERADMIN] = (accountId: string) =>
    new Url(`superadmin/account/${accountId}`, "superadmin/account/${accountId}")
  static readonly [EndpointTypes.POST_ACCOUNT] = () => new Url("/account", "/account")
  static readonly [EndpointTypes.ACCOUNT_PICTURE] = (accountId: string) =>
    new Url(`admin/account/${accountId}/picture`, "admin/account/${accountId}/picture")
  static readonly [EndpointTypes.ACCOUNT_CONTACT_INFO_SUPER_ADMIN] = (accountId: string) =>
    new Url(
      `superadmin/account/${accountId}/contactInformation`,
      "superadmin/account/${accountId}/contactInformation"
    )
  static readonly [EndpointTypes.ACCOUNT_CONTACT_INFO] = (accountId: string) =>
    new Url(
      `admin/account/${accountId}/contactInformation`,
      "admin/account/${accountId}/contactInformation"
    )
  static readonly [EndpointTypes.ACCOUNT_RESOLVE] = () =>
    new Url("account/resolve", "account/resolve")
  static readonly [EndpointTypes.ACCOUNT_CHECK] = () => new Url("account/check", "account/check")
  static readonly [EndpointTypes.ACCOUNT_ENABLE] = (accountId: string) =>
    new Url(`admin/account/${accountId}/enabled`, "admin/account/${accountId}/enabled")
  static readonly [EndpointTypes.ACCOUNT_PAYMENT_ENABLE] = (accountId: string) =>
    new Url(
      `superadmin/account/${accountId}/enabledBySubscription`,
      "superadmin/account/${accountId}/enabledBySubscription"
    )
  static readonly [EndpointTypes.ACCOUNT_MCP_ENABLE] = (accountId: string) =>
    new Url(`admin/account/${accountId}/mcp`, "admin/account/${accountId}/mcp")
  static readonly [EndpointTypes.ACCOUNT_BY_ID] = (accountId: string) =>
    new Url(`/account/${accountId}`, "/account/${accountId}")
  static readonly [EndpointTypes.ACCOUNT_SETUP] = (accountId: string) =>
    new Url(`/account/${accountId}/setup`, "/account/${accountId}/setup")
  static readonly [EndpointTypes.ACCOUNT_SETUP_STEP] = (accountId: string) =>
    new Url(`/account/${accountId}/setupStep`, "/account/${accountId}/setupStep")
  static readonly [EndpointTypes.ACCOUNT_COMPLETE_SETUP] = (accountId: string) =>
    new Url(`admin/account/${accountId}/completeSetup`, "admin/account/${accountId}/completeSetup")
  static readonly [EndpointTypes.ACCOUNT_TEST] = (accountId: string) =>
    new Url(`superadmin/account/${accountId}/test`, "admin/account/${accountId}/test")

  // user
  static readonly [EndpointTypes.USER_CURRENT] = () => new Url("/user/current", "/user/current")
  static readonly [EndpointTypes.USER_CURRENT_ROLE] = role =>
    new Url(`/user/current/roles/${role}`, "/user/current/roles/${role}")
  static readonly [EndpointTypes.USER_CURRENT_TIME_ZONE] = () =>
    new Url("/user/current/settings/timeZone", "/user/current/settings/timeZone")
  static readonly [EndpointTypes.USER_CURRENT_PICTURE] = () =>
    new Url("/user/current/picture", "/user/current/picture")
  static readonly [EndpointTypes.USER_CURRENT_PROFILE] = () =>
    new Url("/user/current/profile", "/user/current/profile")
  static readonly [EndpointTypes.UPDATE_PROFILE_SETTINGS] = () =>
    new Url("/user/current/settings", "/user/current/settings")
  static readonly [EndpointTypes.USER_CURRENT_ACCOUNTS] = () =>
    new Url("/user/current/accounts", "/user/current/accounts")
  static readonly [EndpointTypes.USER_CURRENT_PROJECTS_BY_ACCOUNT] = (accountId: string) =>
    new Url(
      `/user/current/account/${accountId}/projects`,
      "/user/current/account/${accountId}/projects"
    )
  static readonly [EndpointTypes.GET_USERS] = (accountId: string) =>
    new Url(`/account/${accountId}/v2/user`, "/account/${accountId}/v2/user")
  static readonly [EndpointTypes.GET_USERS_EXTENDED] = (accountId: string) =>
    new Url(`/account/${accountId}/v2/user/extended`, "/account/${accountId}/v2/user/extended")
  static readonly [EndpointTypes.GET_USER_COUNTS] = (accountId: string, teamId?: string) =>
    new Url(
      teamId
        ? `/account/${accountId}/v2/user/counts?teamId=${teamId}`
        : `/account/${accountId}/v2/user/counts`,
      "/account/${accountId}/v2/user/counts"
    )
  static readonly [EndpointTypes.GET_USER_BY_ID] = (id: string) =>
    new Url(`/user/${id}`, "/user/${id}")
  static readonly [EndpointTypes.ADMIN_USER_BY_ID] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}`,
      "/account/${accountId}/v1/admin/users/${userId}"
    )
  static readonly [EndpointTypes.SUPERADMIN_USER_BY_ID] = (accountId: string, userId: string) =>
    new Url(
      `/superadmin/user/${userId}/account/${accountId}`,
      "/superadmin/user/${userId}/account/${accountId}"
    )

  static readonly [EndpointTypes.USER_PICTURE_UPDATE] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}/picture`,
      "/account/${accountId}/v1/admin/users/${userId}/picture"
    )
  static readonly [EndpointTypes.USER_SETTINGS] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}/settings`,
      "/account/${accountId}/v1/admin/users/${userId}/settings"
    )

  //Stripe
  static readonly [EndpointTypes.STRIPE_GET_PRODUCTS] = () =>
    new Url(`/stripe/products`, "/stripe/products")

  static readonly [EndpointTypes.STRIPE_GET_PRICES] = () =>
    new Url(`/stripe/prices`, "/stripe/prices")

  static readonly [EndpointTypes.STRIPE_SUBSCRIBE] = () =>
    new Url(`/stripe/accept`, "/stripe/accept")
  static readonly [EndpointTypes.PAYMENT_LINK] = (accountId: string) =>
    new Url(`/stripe/${accountId}/payment-link`, "/stripe/${accountId}/payment-link")
  static readonly [EndpointTypes.SET_STRIPE_CUSTOMER_ID] = (
    accountId: string,
    customerId: string
  ) =>
    new Url(
      `/admin/account/${accountId}/customer/${customerId}`,
      "/admin/account/${accountId}/customer/${customerId}"
    )

  static readonly [EndpointTypes.PAYMENT_SUBSCRIPTION_START] = accountId =>
    new Url(
      `/admin/account/${accountId}/paymentSubscription/startSubscription`,
      "/admin/account/${accountId}/paymentSubscription/startSubscription"
    )

  static readonly [EndpointTypes.PAYMENT_SUBSCRIPTION_START_TRIAL] = accountId =>
    new Url(
      `/admin/account/${accountId}/paymentSubscription/startTrial`,
      "/admin/account/${accountId}/paymentSubscription/startTrial"
    )

  // userManagement
  static readonly [EndpointTypes.GET_ALL_USERS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/admin/users`, "/account/${accountId}/v1/admin/users")
  static readonly [EndpointTypes.GET_ALL_USERS_SUPERADMIN] = () =>
    new Url("/superadmin/user/search", "/superadmin/user/search")
  static readonly [EndpointTypes.USER_BY_ID] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}`,
      "/account/${accountId}/v1/admin/users/${userId}"
    )
  static readonly [EndpointTypes.USER_ADD_PROJECT] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}/projects`,
      "/account/${accountId}/v1/admin/users/${userId}/projects"
    )
  static readonly [EndpointTypes.USER_DEACTIVATION] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/admin/users`, "/account/${accountId}/v1/admin/users")
  static readonly [EndpointTypes.USER_PERMISSIONS] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}/permissions`,
      "/account/${accountId}/v1/admin/users/${userId}/permissions"
    )
  static readonly [EndpointTypes.ADMIN_USER_EMAIL_SUPPRESSION] = (
    accountId: string,
    userId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}/email-suppression`,
      "/account/${accountId}/v1/admin/users/${userId}/email-suppression"
    )

  // invitations
  static readonly [EndpointTypes.INVITATION] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation`,
      "/account/${accountId}/v1/admin/invitation"
    )
  static readonly [EndpointTypes.INVITATION_WITH_FEEDBACK] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/users`,
      "/account/${accountId}/v1/admin/invitation/users"
    )
  static readonly [EndpointTypes.INVITATION_CHECK] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/check`,
      "/account/${accountId}/v1/admin/invitation/check"
    )
  static readonly [EndpointTypes.INVITATION_WITH_TEAM_CHECK] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/teamId/${teamId}/check`,
      "/account/${accountId}/v1/admin/invitation/teamId/${teamId}/check"
    )
  static readonly [EndpointTypes.INVITATION_RESEND_BY_ID] = (accountId: string, id: string) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/${id}/resend`,
      "/account/${accountId}/v1/admin/invitation/${id}/resend"
    )
  static readonly [EndpointTypes.INVITATION_RESEND_BY_EMAIL] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/resend`,
      "/account/${accountId}/v1/admin/invitation/resend"
    )
  static readonly [EndpointTypes.INVITATION_ACCEPT] = (id: string) =>
    new Url(`/invitation/${id}/accept`, "/invitation/${id}/accept")
  static readonly [EndpointTypes.INVITATION_CANCEL] = (accountId: string, id: string) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/${id}/cancel`,
      "/account/${accountId}/v1/admin/invitation/${id}/cancel"
    )
  static readonly [EndpointTypes.INVITATION_UPDATE_TEAM_ROLE] = (
    accountId: string,
    invitationId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/${invitationId}/team-role`,
      "/account/${accountId}/v1/admin/invitation/${invitationId}/team-role"
    )
  static readonly [EndpointTypes.INVITE_TO_TEAM_LINK] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/invitation/team/invitation/link`,
      "/account/${accountId}/v1/admin/invitation/team/invitation/link"
    )

  // project
  static readonly [EndpointTypes.CREATE_DEMO_PROJECT] = (accountId: string) =>
    new Url(`/account/${accountId}/demoProject/v2`, "/account/${accountId}/demoProject/v2")
  static readonly [EndpointTypes.RECREATE_DEMO_PROJECT] = (accountId: string) =>
    new Url(
      `/account/${accountId}/demoProject/v2/recreate`,
      "/account/${accountId}/demoProject/v2/recreate"
    )
  static readonly [EndpointTypes.RECREATE_DEMO_PROJECT_START] = (accountId: string) =>
    new Url(
      `/account/${accountId}/demoProject/v2/recreate/start`,
      "/account/${accountId}/demoProject/v2/recreate/start"
    )
  static readonly [EndpointTypes.GET_DEMO_PROJECTS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/project/demo`, "/account/${accountId}/v1/project/demo")
  static readonly [EndpointTypes.PROJECT] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/project`, "/account/${accountId}/v1/project")
  static readonly [EndpointTypes.DELETE_PROJECT] = (accountId: string, projectId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/project/${projectId}`,
      "/account/${accountId}/v1/admin/project/${projectId}"
    )
  static readonly [EndpointTypes.PROJECT_BY_ID] = (accountId: string, projectId: string) =>
    new Url(
      `/account/${accountId}/v1/project/${projectId}`,
      "/account/${accountId}/v1/project/${projectId}"
    )
  static readonly [EndpointTypes.PROJECT_FAVORITE] = (accountId: string, projectId: string) =>
    new Url(
      `/account/${accountId}/v1/project/${projectId}/favorite`,
      "/account/${accountId}/v1/project/${projectId}/favorite"
    )
  static readonly [EndpointTypes.PROJECT_TEAM_DASHBOARD_HIDDEN] = (
    accountId: string,
    projectId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/project/${projectId}/team-dashboard-hidden`,
      "/account/${accountId}/v1/project/${projectId}/team-dashboard-hidden"
    )
  static readonly [EndpointTypes.ADD_PROJECT_TO_TEAM] = (
    accountId: string,
    projectId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/project/${projectId}/team/${teamId}`,
      "/account/${accountId}/v1/project/${projectId}/team/${teamId}"
    )
  static readonly [EndpointTypes.PROJECT_RESOLVE] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/project/resolve`, "/account/${accountId}/v1/project/resolve")
  static readonly [EndpointTypes.ADMIN_PROJECT_SEARCH] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/project/search`,
      "/account/${accountId}/v1/admin/project/search"
    )
  static readonly [EndpointTypes.PROJECT_SEARCH] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/project/find`, "/account/${accountId}/v1/project/find")
  static readonly [EndpointTypes.CHECK_EXIST_PROJECT_BY_URL_OR_NAME] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/project/existsByNameOrUrlName`,
      "/account/${accountId}/v1/project/existsByNameOrUrlName"
    )
  static readonly [EndpointTypes.ADD_TEAMS_TO_PROJECT] = (accountId: string, projectId: string) =>
    new Url(
      `/account/${accountId}/v1/project/${projectId}/teams`,
      "/account/${accountId}/v1/project/${projectId}/teams"
    )
  // project overview
  static readonly [EndpointTypes.PROJECT_OVERVIEW] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/projectOverview`, "/account/${accountId}/v1/projectOverview")
  static readonly [EndpointTypes.PROJECT_OVERVIEW_BY_USER] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/projectWithStats/user/${userId}/projects`,
      "/account/${accountId}/v1/projectWithStats/user/${userId}/projects"
    )
  static readonly [EndpointTypes.PROJECT_OVERVIEW_SEARCH] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/projectOverview/search`,
      "/account/${accountId}/v1/projectOverview/search"
    )
  static readonly [EndpointTypes.PROJECT_OVERVIEW_EXTENDED] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/projectOverview/extended`,
      "/account/${accountId}/v1/projectOverview/extended"
    )
  static readonly [EndpointTypes.PROJECT_OVERVIEW_EXTENDED_SEARCH] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/projectOverview/extended/search`,
      "/account/${accountId}/v1/projectOverview/extended/search"
    )
  static readonly [EndpointTypes.PROJECT_OVERVIEW_BY_ID] = (accountId: string, projectId: string) =>
    new Url(
      `/account/${accountId}/v1/projectOverview/extended/${projectId}`,
      "/account/${accountId}/v1/projectOverview/extended/${projectId}"
    )
  static readonly [EndpointTypes.PROJECT_OVERVIEW_AT_RISK] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/project/atRisk`, "/account/${accountId}/v1/project/atRisk")

  // admin
  static readonly [EndpointTypes.START_SUBSCRIPTION] = (id: string) =>
    new Url(
      `/admin/account/${id}/paymentSubscription/startCheckout`,
      "/admin/account/${id}/paymentSubscription/startCheckout"
    )
  static readonly [EndpointTypes.CONFIRM_SUBSCRIPTION] = (id: string) =>
    new Url(
      `/admin/account/${id}/paymentSubscription/confirmCheckout`,
      "/admin/account/${id}/paymentSubscription/confirmCheckout"
    )

  // item

  static readonly [EndpointTypes.DUPLICATE_ITEM] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/copy`,
      "/project/${projectId}/v1/item/${itemId}/copy"
    )
  static readonly [EndpointTypes.GET_ITEMS] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/item`, "/project/${projectId}/v1/item")
  static readonly [EndpointTypes.GET_ITEM_ANALYTICS] = (
    accountId: string,
    projectId: string,
    itemId: string
  ) =>
    new Url(
      `/account/${accountId}/protected/access/item/${itemId}/project/${projectId}/analytics`,
      "/account/${accountId}/protected/access/item/${itemId}/project/${projectId}/analytics"
    )
  static readonly [EndpointTypes.MARK_AS_DELETED] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/markAsDeleted`,
      "/project/${projectId}/v1/item/${itemId}/markAsDeleted"
    )
  static readonly [EndpointTypes.GET_ITEM_PROTECTED] = (
    accountId: string,
    projectId: string,
    itemId: string
  ) =>
    new Url(
      `/account/${accountId}/protected/access/item/${itemId}/project/${projectId}`,
      "/account/${accountId}/protected/access/item/${itemId}/project/${projectId}"
    )
  static readonly [EndpointTypes.ITEM_VALIDATE_CHANGE_CHECK] = (
    accountId: string,
    itemId: string,
    projectId: string
  ) =>
    new Url(
      `/account/${accountId}/protected/access/${itemId}/project/${projectId}/validate-change/check`,
      "/account/${accountId}/protected/access/${itemId}/project/${projectId}/validate-change/check"
    )
  static readonly [EndpointTypes.POST_ITEM] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/item`, "/project/${projectId}/v1/item")
  static readonly [EndpointTypes.ITEM_VALIDATE_CREATE_CHECK] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/item/validate-create/check`,
      "/project/${projectId}/v1/item/validate-create/check"
    )
  static readonly [EndpointTypes.CHANGE_STATUS_ITEM] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/changeStatus/includingMetadata`,
      "/project/${projectId}/v1/item/${itemId}/changeStatus/includingMetadata"
    )
  static readonly [EndpointTypes.CHANGE_OWNER_ITEM] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/owner`,
      "/project/${projectId}/v1/item/${itemId}/owner"
    )
  static readonly [EndpointTypes.GET_ITEM_FILES] = (
    accountId: string,
    projectId: string,
    itemId: string
  ) =>
    new Url(
      `/account/${accountId}/protected/access/project/${projectId}/${itemId}/fileMetadata`,
      "/account/${accountId}/protected/access/project/${projectId}/${itemId}/fileMetadata"
    )
  static readonly [EndpointTypes.ITEM_DEFINITION_OF_DONE] = (
    projectId: string,
    itemId: string,
    definitionOfDoneId: string
  ) =>
    new Url(
      `/api/v1/project/${projectId}/v1/item/${itemId}/definitionOfDone/${definitionOfDoneId}`,
      "/api/v1/project/${projectId}/v1/item/${itemId}/definitionOfDone/${definitionOfDoneId}"
    )
  static readonly [EndpointTypes.ASSIGN_EPIC] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/item/epic/${epicId}`,
      "/project/${projectId}/v1/item/epic/${epicId}"
    )
  static readonly [EndpointTypes.GET_ITEMS_SNAPSHOT_BY_EPIC_V2] = (
    projectId: string,
    epicId: string,
    milestoneId: string
  ) =>
    new Url(
      `/project/${projectId}/v2/item/epic/${epicId}/milestone/${milestoneId}`,
      "/project/${projectId}/v2/item/epic/${epicId}/milestone/${milestoneId}"
    )
  static readonly [EndpointTypes.DELETE_ITEM_FROM_EPIC] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/item/epic/${epicId}`,
      "/project/${projectId}/v1/item/epic/${epicId}"
    )
  static readonly [EndpointTypes.DELETE_ITEM_FILE] = (
    projectId: string,
    itemId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/fileMetadata/${fileMetadataId}`,
      "/project/${projectId}/v1/item/${itemId}/fileMetadata/${fileMetadataId}"
    )
  static readonly [EndpointTypes.RESOLVE_ITEM_ID] = (
    accountId,
    projectId: string,
    sequenceNumber: number
  ) =>
    new Url(
      `/account/${accountId}/protected/access/project/${projectId}/resolve/${sequenceNumber}`,
      "/account/${accountId}/protected/access/project/${projectId}/resolve/${sequenceNumber}"
    )
  static readonly [EndpointTypes.MOVE_TO_ITEM] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/taskMoveTo`,
      "/project/${projectId}/v1/item/${itemId}/taskMoveTo"
    )
  static readonly [EndpointTypes.UPDATE_ITEM_READINESS] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/readiness`,
      "/project/${projectId}/v1/item/${itemId}/readiness"
    )
  static readonly [EndpointTypes.IMPORT_ITEM_TO_EPIC] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/item/epic/${epicId}`,
      "/project/${projectId}/v1/item/epic/${epicId}"
    )
  static readonly [EndpointTypes.CHANGE_TEAM_FOR_ITEMS_BATCH] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/item/team`, "/project/${projectId}/v1/item/team")
  static readonly [EndpointTypes.DEFINITION_OF_DONE] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/definitionOfDone`,
      "/project/${projectId}/v1/item/${itemId}/definitionOfDone"
    )
  static readonly [EndpointTypes.TASKS_LIST] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/tasks`,
      "/project/${projectId}/v1/item/${itemId}/tasks"
    )
  static readonly [EndpointTypes.TASKS_ORDER] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/tasks/order`,
      "/project/${projectId}/v1/item/${itemId}/tasks/order"
    )
  static readonly [EndpointTypes.TASKS_PROVIDED] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/provided/tasks`,
      "/project/${projectId}/v1/item/${itemId}/provided/tasks"
    )

  static readonly [EndpointTypes.RESTORE_TASK] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/restoreTask`,
      "/project/${projectId}/v1/item/${itemId}/restoreTask"
    )

  static readonly [EndpointTypes.DELETE_TASK] = (
    projectId: string,
    itemId: string,
    taskId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/tasks/${taskId}`,
      "/project/${projectId}/v1/item/${itemId}/tasks/${taskId}"
    )
  static readonly [EndpointTypes.TASKS_ESTIMATION] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/tasks/estimation`,
      "/project/${projectId}/v1/item/${itemId}/tasks/estimation"
    )
  static readonly [EndpointTypes.UPDATE_ITEM_DETAILS] = (projectId: string, itemId: string) =>
    new Url(`/project/${projectId}/v1/item/${itemId}`, "/project/${projectId}/v1/item/${itemId}")
  static readonly [EndpointTypes.UPDATE_TEAM] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/team`,
      "/project/${projectId}/v1/item/${itemId}/team"
    )
  static readonly [EndpointTypes.UPDATE_ITEM_TEAM_TYPE] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/team-type`,
      "/project/${projectId}/v1/item/${itemId}/team-type"
    )
  static readonly [EndpointTypes.GET_ITEM_TEAM_TYPES] = (projectId: string) =>
    new Url(`/project/${projectId}/v2/item/team-types`, "/project/${projectId}/v2/item/team-types")
  static readonly [EndpointTypes.CHANGE_SPRINT_PRIORITY] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/item/${itemId}/sprintChange`,
      "/project/${projectId}/v1/sprint/item/${itemId}/sprintChange"
    )
  static readonly [EndpointTypes.GET_ITEM_PRIORITY] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/priority`,
      "/project/${projectId}/v1/item/${itemId}/priority"
    )
  static readonly [EndpointTypes.GET_ITEM_CONFIG] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/config/item`, "/account/${accountId}/v1/config/item")
  // comment
  static readonly [EndpointTypes.COMMENT_MARK_AS_DELETED] = (
    accountId: string,
    commentId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/comment/markAsDeleted/${commentId}`,
      "/account/${accountId}/v1/comment/markAsDeleted/${commentId}"
    )
  static readonly [EndpointTypes.GET_COMMENTS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/comment`, "/account/${accountId}/v1/comment")
  static readonly [EndpointTypes.GET_COMMENT] = (accountId: string, commentId: string) =>
    new Url(
      `/account/${accountId}/v1/comment/${commentId}`,
      "/account/${accountId}/v1/comment/${commentId}"
    )
  static readonly [EndpointTypes.GET_COMMENTS_BY_SOURCE] = (
    accountId: string,
    sourceId: string,
    sourceType: string
  ) =>
    new Url(
      `/account/${accountId}/v1/comment/source/${sourceId}/${sourceType}`,
      "/account/${accountId}/v1/comment/source/${sourceId}/${sourceType}"
    )
  static readonly [EndpointTypes.POST_COMMENT] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/comment`, "/account/${accountId}/v1/comment")
  static readonly [EndpointTypes.UPDATE_COMMENT_DESCRIPTION] = (
    accountId: string,
    commentId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/comment/${commentId}`,
      "/account/${accountId}/v1/comment/${commentId}"
    )
  static readonly [EndpointTypes.UPDATE_STATUS_DESCRIPTION] = (
    accountId: string,
    commentId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/comment/${commentId}/structuredDescription`,
      "/account/${accountId}/v1/comment/${commentId}/structuredDescription"
    )
  static readonly [EndpointTypes.GET_ITEMS_COUNT_WITHOUT_SUCCESS] = (accountId: string) =>
    new Url(
      `/account/${accountId}/item/search/project-close-summary`,
      "/account/${accountId}/item/search/project-close-summary"
    )
  static readonly [EndpointTypes.GET_COMMENT_COUNT] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/comment/count`, "/account/${accountId}/v1/comment/count")
  static readonly [EndpointTypes.COMMENT_REACTION] = (accountId: string, commentId: string) =>
    new Url(
      `/account/${accountId}/v1/comment/${commentId}/reaction`,
      "/account/${accountId}/v1/comment/${commentId}/reaction"
    )
  static readonly [EndpointTypes.CONVERT_ITEM_TO_EPIC] = (
    accountId: string,
    itemId: string,
    projectId: string
  ) =>
    new Url(
      `/account/${accountId}/protected/access/${itemId}/project/${projectId}/convertToEpic`,
      "/account/${accountId}/protected/access/${itemId}/project/${projectId}/convertToEpic"
    )

  // task
  // toDo
  static readonly [EndpointTypes.TODO] = (accountId: string) =>
    new Url(`/account/${accountId}/v2/todoItem`, "/account/${accountId}/v2/todoItem")
  static readonly [EndpointTypes.TODO_ITEM_RESOLVE] = (accountId: string, todoItemId: string) =>
    new Url(
      `/account/${accountId}/v2/todoItem/${todoItemId}/resolve`,
      "/account/${accountId}/v2/todoItem/${todoItemId}/resolve"
    )
  static readonly [EndpointTypes.TODO_ITEM_UNRESOLVE] = (accountId: string, todoItemId: string) =>
    new Url(
      `/account/${accountId}/v2/todoItem/${todoItemId}/unresolve`,
      "/account/${accountId}/v2/todoItem/${todoItemId}/unresolve"
    )
  static readonly [EndpointTypes.TODO_COUNT] = (accountId: string) =>
    new Url(`/account/${accountId}/v2/todoItem/count`, "/account/${accountId}/v2/todoItem/count")
  static readonly [EndpointTypes.TODO_METRICS] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/todoItem/metrics`,
      "/account/${accountId}/v2/todoItem/metrics"
    )
  static readonly [EndpointTypes.TODO_ITEM] = (accountId: string, todoItemId: string) =>
    new Url(
      `/account/${accountId}/v2/todoItem/${todoItemId}`,
      "/account/${accountId}/v2/todoItem/${todoItemId}"
    )
  static readonly [EndpointTypes.TODO_ITEM_DELETE_ATTACHMENT] = (
    accountId: string,
    todoItemId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/todoItem/${todoItemId}/fileMetadata/${fileMetadataId}`,
      "/account/${accountId}/v2/todoItem/${todoItemId}/fileMetadata/${fileMetadataId}"
    )

  // attachment
  static readonly [EndpointTypes.ATTACHMENT_UPLOAD_TARGET] = (
    accountId: string,
    resourceType: ResourceType,
    targetId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${resourceType}/${targetId}`,
      "/account/${accountId}/v2/attachment/${resourceType}/${targetId}"
    )
  static readonly [EndpointTypes.ATTACHMENT_UPLOAD_ITEM_DEMO_VIDEO] = (
    accountId: string,
    projectId: string,
    resourceType: ResourceType,
    targetId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/project/${projectId}/${resourceType}/${targetId}/demo-video`,
      "/account/${accountId}/v2/attachment/project/${projectId}/${resourceType}/${targetId}/demo-video"
    )
  static readonly [EndpointTypes.GET_DEMO_VIDEOS] = (
    accountId: string,
    projectId: string,
    resourceType: ResourceType,
    targetId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/demo-video/project/${projectId}/${resourceType}/${targetId}`,
      "/account/${accountId}/v2/demo-video/project/${projectId}/${resourceType}/${targetId}"
    )
  static readonly [EndpointTypes.CREATE_DEMO_VIDEO] = (
    accountId: string,
    projectId: string,
    resourceType: ResourceType,
    targetId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/demo-video/project/${projectId}/${resourceType}/${targetId}`,
      "/account/${accountId}/v2/demo-video/project/${projectId}/${resourceType}/${targetId}"
    )
  static readonly [EndpointTypes.DELETE_DEMO_VIDEO] = (
    accountId: string,
    projectId: string,
    demoVideoId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/demo-video/project/${projectId}/${demoVideoId}`,
      "/account/${accountId}/v2/demo-video/project/${projectId}/${demoVideoId}"
    )
  static readonly [EndpointTypes.ATTACHMENT_UPLOAD] = (accountId: string) =>
    new Url(`/account/${accountId}/v2/attachment/`, "/account/${accountId}/v2/attachment/")
  static readonly [EndpointTypes.DELETE_FILE] = (accountId: string, fileMetadataId: string) =>
    new Url(
      `/account/${accountId}/v2/attachment/${fileMetadataId}`,
      "/account/${accountId}/v2/attachment/${fileMetadataId}"
    )
  static readonly [EndpointTypes.ATTACHMENT_STREAM_TOKEN] = (
    accountId: string,
    projectId: string,
    attachmentId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${attachmentId}${projectId ? `/projectId/${projectId}` : ""}/stream-token`,
      "/account/${accountId}/v2/attachment/${attachmentId}/projectId/${projectId}/stream-token"
    )
  static readonly [EndpointTypes.ATTACHMENT_DIRECT_UPLOAD_START] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/attachment/uploads`,
      "/account/${accountId}/v2/attachment/uploads"
    )
  static readonly [EndpointTypes.ATTACHMENT_DIRECT_UPLOAD_PART] = (
    accountId: string,
    partNumber: number
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/uploads/parts/${partNumber}`,
      "/account/${accountId}/v2/attachment/uploads/parts/${partNumber}"
    )
  static readonly [EndpointTypes.ATTACHMENT_DIRECT_UPLOAD_COMPLETE] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/attachment/uploads/complete`,
      "/account/${accountId}/v2/attachment/uploads/complete"
    )
  static readonly [EndpointTypes.ATTACHMENT_DIRECT_UPLOAD_CANCEL] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/attachment/uploads/cancel`,
      "/account/${accountId}/v2/attachment/uploads/cancel"
    )

  static readonly [EndpointTypes.ATTACHMENT_VIDEO_URL] = (
    accountId: string,
    projectId: string,
    attachmentId: string,
    token: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${attachmentId}${projectId ? `/projectId/${projectId}` : ""}/streaming?token=${encodeURIComponent(token)}`,
      "/account/${accountId}/v2/attachment/${attachmentId}/projectId/${projectId}/streaming?token=${token}"
    )

  static readonly [EndpointTypes.ATTACHMENT_VIDEO_URL_BY_ACCOUNT] = (
    accountId: string,
    attachmentId: string,
    token: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${attachmentId}/streaming?token=${encodeURIComponent(token)}`,
      "/account/${accountId}/v2/attachment/${attachmentId}/streaming?token=${token}"
    )
  // attachment by account
  static readonly [EndpointTypes.ATTACHMENT_UPLOAD_TARGET_BY_ACCOUNT] = (
    accountId: string,
    resourceType: ResourceType,
    targetId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${resourceType}/${targetId}`,
      "/account/${accountId}/v2/attachment/${resourceType}/${targetId}"
    )
  static readonly [EndpointTypes.ATTACHMENT_UPLOAD_BY_ACCOUNT] = (accountId: string) =>
    new Url(`/account/${accountId}/v2/attachment/`, "/account/${accountId}/v2/attachment/")
  static readonly [EndpointTypes.ATTACHMENT_BY_ACCOUNT] = (
    accountId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${fileMetadataId}/preview`,
      "/account/${accountId}/v2/attachment/${fileMetadataId}/preview"
    )
  static readonly [EndpointTypes.ATTACHMENT_DOCUMENT_PREVIEW_BY_ACCOUNT] = (
    accountId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${fileMetadataId}/document-preview`,
      "/account/${accountId}/v2/attachment/${fileMetadataId}/document-preview"
    )
  static readonly [EndpointTypes.ATTACHMENT_DOWNLOAD_BY_ACCOUNT] = (
    accountId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${fileMetadataId}`,
      "/account/${accountId}/v2/attachment/${fileMetadataId}"
    )
  static readonly [EndpointTypes.DELETE_ATTACHMENT_BY_ACCOUNT] = (
    accountId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${fileMetadataId}`,
      "/account/${accountId}/v2/attachment/${fileMetadataId}"
    )
  static readonly [EndpointTypes.DELETE_ATTACHMENT_FROM_TARGET] = (
    accountId: string,
    resourceType: ResourceType,
    targetId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${resourceType}/${targetId}/fileMetadata/${fileMetadataId}`,
      "/account/${accountId}/v2/attachment/${resourceType}/${targetId}/fileMetadata/${fileMetadataId}"
    )
  static readonly [EndpointTypes.ATTACHMENT_FILE_METADATA] = (
    accountId: string,
    resourceType: ResourceType,
    targetId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${resourceType}/${targetId}/fileMetadata`,
      "/account/${accountId}/v2/attachment/${resourceType}/${targetId}/fileMetadata"
    )
  static readonly [EndpointTypes.ATTACHMENT_METADATA] = (
    accountId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/account/${accountId}/v2/attachment/${fileMetadataId}/metadata`,
      "/account/${accountId}/v2/attachment/${fileMetadataId}/metadata"
    )

  // team
  static readonly [EndpointTypes.TEAMS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/team`, "/account/${accountId}/v1/team")
  static readonly [EndpointTypes.TEAMS_WITH_USERS_SEARCH] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/team/extended/search`,
      "/account/${accountId}/v1/team/extended/search"
    )
  static readonly [EndpointTypes.TEAMS_SEARCH_ADMIN] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/team/search`,
      "/account/${accountId}/v1/admin/team/search"
    )
  static readonly [EndpointTypes.TEAM_UPDATE_ADMIN] = (accountId: string, teamId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/team/${teamId}`,
      "/account/${accountId}/v1/admin/team/${teamId}"
    )
  static readonly [EndpointTypes.TEAM] = (accountId: string, teamId: string) =>
    new Url(`/account/${accountId}/v1/team/${teamId}`, "/account/${accountId}/v1/team/${teamId}")
  static readonly [EndpointTypes.TEAM_FAVORITE] = (teamId: string) =>
    new Url(`/user/team/${teamId}/favorite`, "/user/team/${teamId}/favorite")
  static readonly [EndpointTypes.CHECK_TEAM_NAME] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/team/find`, "/account/${accountId}/v1/team/find")
  static readonly [EndpointTypes.TEAM_RESOLVE] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/team/resolve`, "/account/${accountId}/v1/team/resolve")
  static readonly [EndpointTypes.TEAM_PICTURE] = (accountId: string, teamId: string) =>
    new Url(
      `/account/${accountId}/v1/team/${teamId}/picture`,
      "/account/${accountId}/v1/team/${teamId}/picture"
    )
  static readonly [EndpointTypes.TEAM_IN_PROJECT] = (accountId: string, projectId: string) =>
    new Url(
      `/account/${accountId}/v1/team/project/${projectId}`,
      "/account/${accountId}/v1/team/project/${projectId}"
    )
  static readonly [EndpointTypes.TEAM_IN_PROJECT_EXTENDED] = (
    accountId: string,
    projectId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/team/extended/project/${projectId}`,
      "/account/${accountId}/v1/team/extended/project/${projectId}"
    )
  static readonly [EndpointTypes.SEARCH_PROJECT_RESOURCE] = (
    accountId: string,
    projectId: string,
    resourceType: ResourceType
  ) =>
    new Url(
      `/account/${accountId}/v1/team/search/project/${projectId}/resource/${resourceType}`,
      "/account/${accountId}/v1/team/search/project/${projectId}/resource/${resourceType}"
    )
  static readonly [EndpointTypes.GET_EPICS_FILTER_TEAMS] = (accountId: string, projectId: string) =>
    new Url(
      `/account/${accountId}/v1/team/search/project/${projectId}/epics-filter`,
      "/account/${accountId}/v1/team/search/project/${projectId}/epics-filter"
    )
  static readonly [EndpointTypes.TEAM_USER_ADD] = (accountId: string, teamId: string) =>
    new Url(
      `/account/${accountId}/v1/team/${teamId}/user`,
      "/account/${accountId}/v1/team/${teamId}/user"
    )
  static readonly [EndpointTypes.TEAM_USER_DELETE] = (accountId: string, teamId: string, userId) =>
    new Url(
      `/account/${accountId}/v1/team/${teamId}/user/${userId}`,
      "/account/${accountId}/v1/team/${teamId}/user/${userId}"
    )
  static readonly [EndpointTypes.TEAM_USER_UPDATE_ROLE] = (
    accountId: string,
    teamId: string,
    userId: string,
    role: string
  ) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}/team/${teamId}/role/${role}`,
      "/account/${accountId}/v1/admin/users/${userId}/team/${teamId}/role/${role}"
    )
  static readonly [EndpointTypes.GET_PROJECT_WITH_STATS_TEAM] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/projectWithStats/team/${teamId}`,
      "/account/${accountId}/v1/projectWithStats/team/${teamId}"
    )
  static readonly [EndpointTypes.GET_PROJECT_WITH_STATS_USER] = (
    accountId: string,
    userId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/projectWithStats/user/${userId}`,
      "/account/${accountId}/v1/projectWithStats/user/${userId}"
    )
  static readonly [EndpointTypes.TEAM_CREATE_WITH_USERS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/team/bulk`, "/account/${accountId}/v1/team/bulk")
  static readonly [EndpointTypes.TEAM_WITH_EXTENDED_USERS] = (accountId: string, teamId: string) =>
    new Url(
      `/account/${accountId}/v1/team/${teamId}/extended`,
      "/account/${accountId}/v1/team/${teamId}/extended"
    )
  static readonly [EndpointTypes.TEAM_WITH_AUTHORIZED_USERS] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/team/${teamId}/extended/participants`,
      "/account/${accountId}/v1/team/${teamId}/extended/participants"
    )
  static readonly [EndpointTypes.TEAM_HAS_ACTIVE_SPRINT] = (
    accountId: string,
    teamId: string,
    projectId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/team/${teamId}/project/${projectId}/activeSprint`,
      "/account/${accountId}/v1/team/${teamId}/project/${projectId}/activeSprint"
    )
  static readonly [EndpointTypes.TEAM_ROLES] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/team/roles`, "/account/${accountId}/v1/team/roles")

  // picture
  static readonly [EndpointTypes.PICTURE] = () => new Url("/picture", "/picture")

  // backlog
  static readonly [EndpointTypes.BACKLOG] = (projectId: string) =>
    new Url(`/project/${projectId}/v2/item`, "/project/${projectId}/v2/item")
  static readonly [EndpointTypes.GET_EXTENDED_ITEMS] = (projectId: string) =>
    new Url(`/project/${projectId}/v2/item/extended`, "/project/${projectId}/v2/item/extended")
  static readonly [EndpointTypes.GET_ITEM_CURRENT_POSITION] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v2/item/${itemId}/position`,
      "/project/${projectId}/v1/item/${itemId}"
    )
  static readonly [EndpointTypes.CHANGE_ITEM_PRIORITY] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/item/changePriority`,
      "/project/${projectId}/v1/item/changePriority"
    )
  static readonly [EndpointTypes.CHANGE_ITEM_PRIORITY_BATCH] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/item/changePriorityBatch`,
      "/project/${projectId}/v1/item/changePriorityBatch"
    )
  static readonly [EndpointTypes.BACKLOG_ANALYTICS] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/metrics/backlogs/analytics`,
      "/project/${projectId}/v1/metrics/backlogs/analytics"
    )
  static readonly [EndpointTypes.BACKLOG_ITEMS_AGE] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/metrics/backlogs/items/age`,
      "/project/${projectId}/v1/metrics/backlogs/items/age"
    )

  // epic
  static readonly [EndpointTypes.GET_EPIC_DETAILS] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/details`,
      "/project/${projectId}/v1/epic/${epicId}/details"
    )
  static readonly [EndpointTypes.GET_EPIC] = (projectId: string, epicId: string) =>
    new Url(`/project/${projectId}/v1/epic/${epicId}`, "/project/${projectId}/v1/epic/${epicId}")
  static readonly [EndpointTypes.GET_EPIC_CURRENT_POSITION] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/position`,
      "/project/${projectId}/v1/epic/${epicId}/position"
    )
  static readonly [EndpointTypes.GET_EPICS] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/epic`, "/project/${projectId}/v1/epic")
  static readonly [EndpointTypes.POST_EPIC] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/epic`, "/project/${projectId}/v1/epic")
  static readonly [EndpointTypes.UPDATE_EPIC] = (projectId: string, epicId: string) =>
    new Url(`/project/${projectId}/v1/epic/${epicId}`, "/project/${projectId}/v1/epic/${epicId}")
  static readonly [EndpointTypes.CHANGE_EPIC_PRIORITY] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/changePriority`,
      "/project/${projectId}/v1/epic/changePriority"
    )
  static readonly [EndpointTypes.CHANGE_EPIC_PRIORITY_IN_MILESTONE] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/changeMilestonePriority`,
      "/project/${projectId}/v1/epic/changeMilestonePriority"
    )
  static readonly [EndpointTypes.LIST_OF_MILESTONE_CHARTS] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/milestone/preview/includingMetadata`,
      "/project/${projectId}/v1/milestone/preview/includingMetadata"
    )
  static readonly [EndpointTypes.GET_SIMPLIFY_MILESTONES] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/milestone/search`,
      "/project/${projectId}/v1/milestone/search"
    )
  static readonly [EndpointTypes.MOVE_EPIC_TO_MILESTONE] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/moveToMilestone`,
      "/project/${projectId}/v1/epic/moveToMilestone"
    )
  static readonly [EndpointTypes.EPIC_MILESTONE_PLANNING] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/milestone/planning`,
      "/project/${projectId}/v1/epic/${epicId}/milestone/planning"
    )
  static readonly [EndpointTypes.GET_EPIC_MILESTONE_PLANNING] = (
    projectId: string,
    epicId: string,
    milestoneId: string
  ) =>
    new Url(
      `/project/${projectId}/v2/item/epic/${epicId}/milestone/${milestoneId}/planning`,
      "/project/${projectId}/v2/item/epic/${epicId}/milestone/${milestoneId}/planning"
    )
  static readonly [EndpointTypes.GET_LAST_MILESTONE] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/milestone/last`, "/project/${projectId}/v1/milestone/last")
  static readonly [EndpointTypes.RESOLVE_EPIC_ID] = (projectId: string, sequenceNumber: number) =>
    new Url(
      `/project/${projectId}/v1/epic/resolve/${sequenceNumber}`,
      "/project/${projectId}/v1/epic/resolve/${sequenceNumber}"
    )
  static readonly [EndpointTypes.ARCHIVE_EPIC] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/archive`,
      "/project/${projectId}/v1/epic/${epicId}/archive"
    )
  static readonly [EndpointTypes.MERGE_EPIC] = (
    projectId: string,
    epicId: string,
    targetEpicId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/mergeTo/${targetEpicId}`,
      "/project/${projectId}/v1/epic/${epicId}/mergeTo/${targetEpicId}"
    )
  static readonly [EndpointTypes.RESTORE_EPIC] = (projectId: string, epicId: string) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/restore`,
      "/project/${projectId}/v1/epic/${epicId}/restore"
    )
  static readonly [EndpointTypes.GET_EPIC_FILES] = (
    accountId: string,
    projectId: string,
    epicId: string
  ) =>
    new Url(
      `/account/${accountId}/protected/access/project/${projectId}/epic/${epicId}/fileMetadata`,
      "/account/${accountId}/protected/access/project/${projectId}/epic/${epicId}/fileMetadata"
    )
  static readonly [EndpointTypes.UPDATE_EPIC_STRUCTURED_DESCRIPTION] = (
    projectId: string,
    epicId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/structuredDescription`,
      "/project/${projectId}/v1/epic/${epicId}/structuredDescription"
    )
  static readonly [EndpointTypes.DELETE_FILE_FROM_EPIC] = (
    projectId: string,
    epicId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/epic/${epicId}/fileMetadata/${fileMetadataId}`,
      "/project/${projectId}/v1/epic/${epicId}/fileMetadata/${fileMetadataId}"
    )

  // sprint
  static readonly [EndpointTypes.SPRINTS] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/sprint`, "/project/${projectId}/v1/sprint")
  static readonly [EndpointTypes.CHECK_WIP_LIMIT] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/checkWipLimit`,
      "/project/${projectId}/v1/sprint/${sprintId}/checkWipLimit"
    )
  static readonly [EndpointTypes.IS_WIP_LIMIT_VALID] = (accountId: string, sprintId: string) =>
    new Url(
      `/account/${accountId}/v1/sprint/${sprintId}/isWipLimitValid`,
      "/account/${accountId}/v1/sprint/${sprintId}/isWipLimitValid"
    )
  static readonly [EndpointTypes.SPRINT] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}`,
      "/project/${projectId}/v1/sprint/${sprintId}"
    )
  static readonly [EndpointTypes.SPRINT_NOT_COMPLETED] = (projectId: string, teamId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/team/${teamId}/notCompleted`,
      "/project/${projectId}/v1/sprint/team/${teamId}/notCompleted"
    )
  static readonly [EndpointTypes.RESOLVE_SPRINT_ID] = (
    projectId: string,
    teamId: string,
    sequenceNumber: number
  ) =>
    new Url(
      `/project/${projectId}/v1/sprint/team/${teamId}/resolve/${sequenceNumber}`,
      "/project/${projectId}/v1/sprint/team/${teamId}/resolve/${sequenceNumber}"
    )
  static readonly [EndpointTypes.SPRINT_START] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/start`,
      "/project/${projectId}/v1/sprint/${sprintId}/start"
    )
  static readonly [EndpointTypes.SPRINT_COMPLETE] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/complete`,
      "/project/${projectId}/v1/sprint/${sprintId}/complete"
    )
  static readonly [EndpointTypes.SPRINT_HALT] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/halt`,
      "/project/${projectId}/v1/sprint/${sprintId}/halt"
    )
  static readonly [EndpointTypes.GET_SPRINT_NUMBER] = (projectId: string, teamId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/team/${teamId}/resolve/nextSequenceNumber`,
      "/project/${projectId}/v1/sprint/team/${teamId}/resolve/nextSequenceNumber"
    )
  static readonly [EndpointTypes.SPRINT_START_REVIEW] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/startReview`,
      "/project/${projectId}/v1/sprint/${sprintId}/startReview"
    )
  static readonly [EndpointTypes.SPRINT_COMPLETE_REVIEW] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/completeReview`,
      "/project/${projectId}/v1/sprint/${sprintId}/completeReview"
    )
  static readonly [EndpointTypes.CAN_START_SPRINT] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/canStart`,
      "/project/${projectId}/v1/sprint/${sprintId}/canStart"
    )
  static readonly [EndpointTypes.SPRINT_QUOTE] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/sprint/quote`, "/project/${projectId}/v1/sprint/quote")
  static readonly [EndpointTypes.SPRINT_EXISTS_CONTINUOUS] = (projectId: string, teamId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${teamId}/existsContinuousSprint`,
      "/project/${projectId}/v1/sprint/${teamId}/existsContinuousSprint"
    )
  static readonly [EndpointTypes.EDIT_SUMMARY] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/summary`,
      "/project/${projectId}/v1/sprint/${sprintId}/summary"
    )
  static readonly [EndpointTypes.SPRINT_CAPACITY] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/sprint/capacity`, "/project/${projectId}/v1/sprint/capacity")
  static readonly [EndpointTypes.SPRINT_TIME_OFFS] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/timeOffs`,
      "/project/${projectId}/v1/sprint/${sprintId}/timeOffs"
    )

  static readonly [EndpointTypes.SPRINT_CONTEXT] = (accountId: string, teamId: string) =>
    new Url(
      `/account/${accountId}/v1/sprint/context/${teamId}/onCreation`,
      "/account/${accountId}/v1/sprint/context/${teamId}/onCreation"
    )
  // sprint analytics
  static readonly [EndpointTypes.SPRINT_ANALYTICS] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/analytics`,
      "/project/${projectId}/v1/sprint/${sprintId}/analytics"
    )
  static readonly [EndpointTypes.SPRINT_ANALYTICS_PERFORMANCE] = (
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/analytics/performance`,
      "/project/${projectId}/v1/sprint/${sprintId}/analytics/performance"
    )
  static readonly [EndpointTypes.SPRINT_ANALYTICS_QUALITY] = (
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/analytics/quality`,
      "/project/${projectId}/v1/sprint/${sprintId}/analytics/quality"
    )
  static readonly [EndpointTypes.SPRINT_ANALYTICS_OVERVIEW] = (
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/analytics/overview`,
      "/project/${projectId}/v1/sprint/${sprintId}/analytics/overview"
    )
  static readonly [EndpointTypes.SPRINT_ANALYTICS_TEAM_INSIGHTS] = (
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/analytics/team-insights`,
      "/project/${projectId}/v1/sprint/${sprintId}/analytics/team-insights"
    )
  static readonly [EndpointTypes.SPRINT_ANALYTICS_HEALTH_CHECK] = (
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/analytics/health-check`,
      "/project/${projectId}/v1/sprint/${sprintId}/analytics/health-check"
    )

  // sprint review
  static readonly [EndpointTypes.SPRINT_REVIEW] = (accountId: string, sprintId: string) =>
    new Url(
      `/account/${accountId}/v1/sprintReview/${sprintId}`,
      "/account/${accountId}/v1/sprintReview/${sprintId}"
    )

  // retrospective
  static readonly [EndpointTypes.RETROSPECTIVE_PUT_NOTE] = (
    projectId: string,
    retrospectiveId: string,
    noteId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/note/${noteId}`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/note/${noteId}"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_REVIEWED] = (
    projectId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/notes/reviewed/bulk`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/notes/reviewed/bulk"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_NOTES] = (
    projectId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/note`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/note"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_COMPLETE] = (
    projectId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/complete`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/complete"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_REVEAL] = (
    projectId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/reveal`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/reveal"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_START] = (
    accountId: string,
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/retrospective/sprint/${sprintId}/project/${projectId}/start`,
      "/account/${accountId}/v1/retrospective/sprint/${sprintId}/project/${projectId}/start"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_POST] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/retrospective/sprint/${sprintId}/request`,
      "/project/${projectId}/v1/retrospective/sprint/${sprintId}/request"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_CHANGE_PRIORITY] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/retrospective/changePriority`,
      "/project/${projectId}/v1/retrospective/changePriority"
    )
  static readonly [EndpointTypes.GET_RETROSPECTIVE] = (
    accountId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/retrospective/${retrospectiveId}/includingMetadata`,
      "/account/${accountId}/v1/retrospective/${retrospectiveId}/includingMetadata"
    )
  static readonly [EndpointTypes.GET_RETROSPECTIVE_INSIGHTS] = (
    accountId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/retrospective/${retrospectiveId}/insights`,
      "/account/${accountId}/v1/retrospective/${retrospectiveId}/insights"
    )
  static readonly [EndpointTypes.GET_LIST_OF_RETROSPECTIVE] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/retrospective`, "/account/${accountId}/v1/retrospective")
  static readonly [EndpointTypes.GET_RETROSPECTIVE_BY_SPRINT_ID] = (
    accountId: string,
    sprintId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/retrospective/sprint/${sprintId}/includingMetadata`,
      "/account/${accountId}/v1/retrospective/sprint/${sprintId}/includingMetadata"
    )
  static readonly [EndpointTypes.GET_RETROSPECTIVE_STATUS] = (
    projectId: string,
    sprintId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/sprint/${sprintId}/status`,
      "/project/${projectId}/v1/retrospective/sprint/${sprintId}/status"
    )

  static readonly [EndpointTypes.RETROSPECTIVE_PUT_STACK] = (
    projectId: string,
    retrospectiveId: string,
    stackId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/stack/${stackId}`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/stack/${stackId}"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_POST_STACK] = (
    projectId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/stack`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/stack"
    )
  static readonly [EndpointTypes.RETROSPECTIVE_POST_STACK_NOTE] = (
    projectId: string,
    retrospectiveId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/retrospective/${retrospectiveId}/stack/note`,
      "/project/${projectId}/v1/retrospective/${retrospectiveId}/stack/note"
    )

  // sprints with stats
  static readonly [EndpointTypes.SPRINT_METRICS] = (accountId: string, sprintId: string) =>
    new Url(
      `/account/${accountId}/v1/sprintStats/${sprintId}`,
      "/account/${accountId}/v1/sprintStats/${sprintId}"
    )
  static readonly [EndpointTypes.SPRINT_WITH_STATS] = (accountId: string, sprintId: string) =>
    new Url(
      `/account/${accountId}/v1/sprintWithStats/${sprintId}`,
      "/account/${accountId}/v1/sprintWithStats/${sprintId}"
    )

  static readonly [EndpointTypes.SPRINT_WITH_STATS_FULL] = (accountId: string, sprintId: string) =>
    new Url(
      `/account/${accountId}/v1/sprintWithStats/${sprintId}/full`,
      "/account/${accountId}/v1/sprintWithStats/${sprintId}/full"
    )

  static readonly [EndpointTypes.SPRINTS_WITH_STATS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/sprintWithStats`, "/account/${accountId}/v1/sprintWithStats")

  static readonly [EndpointTypes.SPRINTS_WITH_STATS_EXTENDED] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/sprintWithStats/extended`,
      "/account/${accountId}/v1/sprintWithStats/extended"
    )

  // storypoker
  static readonly [EndpointTypes.GET_STORY_POKER] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/storyPoker/${itemId}`,
      "/project/${projectId}/v1/storyPoker/${itemId}"
    )
  static readonly [EndpointTypes.ADD_STORY_POKER_ESTIMATION] = (
    projectId: string,
    storyPokerId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/storyPoker/${storyPokerId}/estimation`,
      "/project/${projectId}/v1/storyPoker/${storyPokerId}/estimation"
    )
  static readonly [EndpointTypes.CREATE_STORY_POKER] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/storyPoker`, "/project/${projectId}/v1/storyPoker")
  static readonly [EndpointTypes.GET_STORY_POKER_URL] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/storyPokerUrl`,
      "/project/${projectId}/v1/item/${itemId}/storyPokerUrl"
    )
  static readonly [EndpointTypes.COMPLETE_STORY_POKER] = (
    projectId: string,
    storyPokerId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/storyPoker/${storyPokerId}/complete`,
      "/project/${projectId}/v1/storyPoker/${storyPokerId}/complete"
    )

  // backlog metrics
  static readonly [EndpointTypes.METRICS_EPIC] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/metrics/epic`, "/project/${projectId}/v1/metrics/epic")
  static readonly [EndpointTypes.METRICS_SEVERITY] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/metrics/severity`,
      "/project/${projectId}/v1/metrics/severity"
    )
  static readonly [EndpointTypes.METRICS_BUG] = (projectId: string) =>
    new Url(`/project/${projectId}/v2/metrics/bugs`, "/project/${projectId}/v2/metrics/bugs")
  static readonly [EndpointTypes.METRICS_BACKLOG] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/metrics/backlog`, "/project/${projectId}/v1/metrics/backlog")
  static readonly [EndpointTypes.METRICS_TEAM_BACKLOGS] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/metrics/backlogs`,
      "/project/${projectId}/v1/metrics/backlogs"
    )
  static readonly [EndpointTypes.ITEMS_METRICS] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/metrics`, "/project/${projectId}/v1/metrics")
  static readonly [EndpointTypes.ITEMS_METRICS_BY_ACCOUNT] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/items/metrics`, "/account/${accountId}/v1/items/metrics")
  static readonly [EndpointTypes.METRICS_BY_TYPE] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/metrics/type`, "/project/${projectId}/v1/metrics/type")
  // milestone
  static readonly [EndpointTypes.POST_MILESTONE] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/milestone`, "/project/${projectId}/v1/milestone")
  static readonly [EndpointTypes.GET_MILESTONE] = (projectId: string, milestoneId: string) =>
    new Url(
      `/project/${projectId}/v1/milestone/${milestoneId}`,
      "/project/${projectId}/v1/milestone/${milestoneId}"
    )

  static readonly [EndpointTypes.STOP_MILESTONE] = (projectId: string, milestoneId: string) =>
    new Url(
      `/project/${projectId}/v1/milestone/${milestoneId}/stop`,
      "/project/${projectId}/v1/milestone/${milestoneId}/stop"
    )
  static readonly [EndpointTypes.CHANGE_MILESTONE_DURATION] = (
    projectId: string,
    milestoneId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/milestone/${milestoneId}/duration`,
      "/project/${projectId}/v1/milestone/${milestoneId}/duration"
    )
  static readonly [EndpointTypes.GET_MILESTONES] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/milestone`, "/project/${projectId}/v1/milestone")
  static readonly [EndpointTypes.GET_MILESTONES_REVIEW] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/milestoneReview`, "/project/${projectId}/v1/milestoneReview")
  static readonly [EndpointTypes.POST_MILESTONE_EPIC] = (
    projectId: string,
    milestoneId: string,
    epicId: string
  ) =>
    new Url(
      `/project/${projectId}/v1/milestone/${milestoneId}/epic/${epicId}`,
      "/project/${projectId}/v1/milestone/${milestoneId}/epic/${epicId}"
    )
  static readonly [EndpointTypes.MILESTONES_PERIOD] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/milestone/period`,
      "/project/${projectId}/v1/milestone/period"
    )

  // subscription
  static readonly [EndpointTypes.GET_SUBSCRIPTION] = (
    resourceType: ResourceType,
    resourceId: string
  ) =>
    new Url(
      `/subscription/${resourceType}/${resourceId}`,
      "/subscription/${resourceType}/${resourceId}",
      false
    )
  static readonly [EndpointTypes.SET_SUBSCRIPTION] = () => new Url("/subscription", "/subscription")
  static readonly [EndpointTypes.GET_SUBSCRIPTION_TOKEN] = () =>
    new Url("/subscription/slt", "/subscription/slt")
  static readonly [EndpointTypes.STREAM_SUBSCRIPTION] = () =>
    new Url("/api/v1/subscription/stream", "/api/v1/subscription/stream")
  // five-whys
  static readonly [EndpointTypes.FIVE_WHYS_BY_ID] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/fiveWhys/${itemId}`,
      "/project/${projectId}/v1/fiveWhys/${itemId}"
    )
  static readonly [EndpointTypes.FIVE_WHYS] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/fiveWhys`, "/project/${projectId}/v1/fiveWhys")

  // email template
  static readonly [EndpointTypes.GET_EMAIL_TEMPLATES] = () =>
    new Url("/superadmin/emailTemplates", "/superadmin/emailTemplates")
  static readonly [EndpointTypes.GET_EMAIL_TEMPLATE_TYPE] = (type: string) =>
    new Url(`/superadmin/emailTemplates/${type}`, "/superadmin/emailTemplates/${type}")
  static readonly [EndpointTypes.POST_EMAIL_TEMPLATE_RENDER] = (type: string) =>
    new Url(
      `/superadmin/emailTemplates/${type}/render`,
      "/superadmin/emailTemplates/${type}/render"
    )
  static readonly [EndpointTypes.EMAIL_SEND] = (type: string) =>
    new Url(`/superadmin/emailTemplates/${type}/send`, "/superadmin/emailTemplates/${type}/send")

  // search
  static readonly [EndpointTypes.SEARCH] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/search`, "/account/${accountId}/v1/search")
  static readonly [EndpointTypes.SEARCH_PROJECT] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/project/search`, "/account/${accountId}/v1/project/search")
  static readonly [EndpointTypes.SEARCH_TEAM] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/team/search`, "/account/${accountId}/v1/team/search")
  static readonly [EndpointTypes.SEARCH_SPRINT] = (accountId: string) =>
    new Url(`/account/${accountId}/sprint/search`, "/account/${accountId}/sprint/search")
  static readonly [EndpointTypes.SEARCH_ITEM] = (accountId: string) =>
    new Url(`/account/${accountId}/item/search`, "/account/${accountId}/item/search")
  static readonly [EndpointTypes.SEARCH_ITEM_QUERY] = (accountId: string) =>
    new Url(`/account/${accountId}/item/search/query`, "/account/${accountId}/item/search/query")
  static readonly [EndpointTypes.SEARCH_ITEM_WITH_TASKS] = (projectId: string, itemId: string) =>
    new Url(
      `/project/${projectId}/v1/item/${itemId}/preview`,
      "/project/${projectId}/v1/item/${itemId}/preview"
    )
  static readonly [EndpointTypes.SEARCH_EPIC] = (accountId: string) =>
    new Url(`/account/${accountId}/epic/search`, "/account/${accountId}/epic/search")
  static readonly [EndpointTypes.SEARCH_IDEA] = (accountId: string) =>
    new Url(`/account/${accountId}/idea/search`, "/account/${accountId}/idea/search")
  static readonly [EndpointTypes.SEARCH_MILESTONE] = (accountId: string) =>
    new Url(`/account/${accountId}/milestone/search`, "/account/${accountId}/milestone/search")
  static readonly [EndpointTypes.GET_MILESTONE_EXCLUDING_COMING_STATUS] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v1/milestone/searchExcludingComing`,
      "/project/${projectId}/v1/milestone/searchExcludingComing"
    )
  static readonly [EndpointTypes.SEARCH_LABELS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/label`, "/account/${accountId}/v1/label")

  // my work
  static readonly [EndpointTypes.MY_WORK_PRIORITIZED] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/myWork/prioritized/includingMetadata/v2`,
      "/account/${accountId}/v1/myWork/prioritized/includingMetadata/v2"
    )
  static readonly [EndpointTypes.MY_WORK_CHANGE_PRIORITY] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/myWork/changePriority`,
      "/account/${accountId}/v1/myWork/changePriority"
    )
  // logging
  static readonly [EndpointTypes.LOG_METRIC_PAGELOAD] = () =>
    new Url("/fe/metric/pageLoad", "/fe/metric/pageLoad")
  static readonly [EndpointTypes.LOG_ERROR] = () => new Url("/fe/log/error", "/fe/log/error", false)

  // notifications
  static readonly [EndpointTypes.NOTIFICATIONS_MARK_AS_READ] = () =>
    new Url("/user/current/notification/markAsRead", "/user/current/notification/markAsRead")
  static readonly [EndpointTypes.GET_NOTIFICATIONS] = () =>
    new Url(
      "/user/current/notification/includingMetadata",
      "/user/current/notification/includingMetadata"
    )

  static readonly [EndpointTypes.SUBMIT_ERROR_REPORT] = () =>
    new Url("/reports/error", "/reports/error")

  // goal
  static readonly [EndpointTypes.POST_GOAL] = (projectId: string) =>
    new Url(`/project/${projectId}/v2/goal`, "/project/${projectId}/v2/goal")
  static readonly [EndpointTypes.GET_GOALS_LIST] = (projectId: string) =>
    new Url(`/project/${projectId}/v2/goal/overview`, "/project/${projectId}/v2/goal/overview")
  static readonly [EndpointTypes.GOAL] = (projectId: string, goalId: string) =>
    new Url(`/project/${projectId}/v2/goal/${goalId}`, "/project/${projectId}/v2/goal/${goalId}")
  static readonly [EndpointTypes.GET_EXTENDED_GOAL] = (accountId: string, goalId: string) =>
    new Url(
      `/account/${accountId}/v2/goal/extendedGoal/${goalId}`,
      "/account/${accountId}/v2/goal/extendedGoal/${goalId}"
    )
  static readonly [EndpointTypes.UPDATE_EXTENDED_GOAL] = (projectId: string, goalId: string) =>
    new Url(
      `/project/${projectId}/v2/goal/extendedGoal/${goalId}`,
      "/project/${projectId}/v2/goal/extendedGoal/${goalId}"
    )
  static readonly [EndpointTypes.DELETE_GOAL_ATTACHMENT] = (
    projectId: string,
    goalId: string,
    fileMetadataId: string
  ) =>
    new Url(
      `/project/${projectId}/v2/goal/${goalId}/fileMetadata/${fileMetadataId}`,
      "/project/${projectId}/v2/goal/${goalId}/fileMetadata/${fileMetadataId}"
    )
  static readonly [EndpointTypes.FEATURE_GOAL] = (projectId: string, goalId: string) =>
    new Url(
      `/project/${projectId}/v2/goal/${goalId}/feature`,
      "/project/${projectId}/v2/goal/${goalId}/feature"
    )
  static readonly [EndpointTypes.GET_FEATURE_BY_GOAL] = (projectId: string, goalId: string) =>
    new Url(
      `/project/${projectId}/v2/goal/${goalId}/feature/all`,
      "/project/${projectId}/v2/goal/${goalId}/feature/all"
    )
  static readonly [EndpointTypes.CHANGE_GOAL_PRIORITY] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v2/goal/changePriority`,
      "/project/${projectId}/v2/goal/changePriority"
    )
  static readonly [EndpointTypes.ADD_SPECIFIC_FEATURE] = (
    projectId: string,
    goalId: string,
    featureId: string
  ) =>
    new Url(
      `/project/${projectId}/v2/goal/${goalId}/feature/${featureId}`,
      "/project/${projectId}/v2/goal/${goalId}/feature/${featureId}"
    )
  static readonly [EndpointTypes.SEARCH_GOAL] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/goal/search`, "/account/${accountId}/v1/goal/search")
  static readonly [EndpointTypes.GOAL_STATUS_COUNTS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/goal/search/counts`, "/account/${accountId}/v1/goal/search/counts")
  static readonly [EndpointTypes.GET_GOAL] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/goal`, "/account/${accountId}/v1/goal")
  // idea
  static readonly [EndpointTypes.GET_IDEAS] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/idea`, "/project/${projectId}/v1/idea")
  static readonly [EndpointTypes.GET_IDEA_COUNTS] = (projectId: string) =>
    new Url(`/project/${projectId}/v1/idea/counts`, "/project/${projectId}/v1/idea/counts")
  static readonly [EndpointTypes.RESOLVE_IDEA_ID] = (projectId: string, sequenceNumber: number) =>
    new Url(
      `/project/${projectId}/v1/idea/resolve/${sequenceNumber}`,
      "/project/${projectId}/v1/idea/resolve/${sequenceNumber}"
    )
  static readonly [EndpointTypes.IDEA] = (projectId: string, ideaId: string) =>
    new Url(`/project/${projectId}/v1/idea/${ideaId}`, "/project/${projectId}/v1/idea/${ideaId}")
  static readonly [EndpointTypes.IDEA_VOTE] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/vote`,
      "/project/${projectId}/v1/idea/${ideaId}/vote"
    )
  static readonly [EndpointTypes.IDEA_FOLLOW] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/follow`,
      "/project/${projectId}/v1/idea/${ideaId}/follow"
    )
  static readonly [EndpointTypes.IDEA_READY] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/ready`,
      "/project/${projectId}/v1/idea/${ideaId}/ready"
    )
  static readonly [EndpointTypes.IDEA_CHANGE_PRIORITY] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/changePriority`,
      "/project/${projectId}/v1/idea/${ideaId}/changePriority"
    )
  static readonly [EndpointTypes.IDEA_CHANGE_STATUS] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/changeStatus`,
      "/project/${projectId}/v1/idea/${ideaId}/changeStatus"
    )
  static readonly [EndpointTypes.IDEA_DELETE] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/delete`,
      "/project/${projectId}/v1/idea/${ideaId}/delete"
    )
  static readonly [EndpointTypes.IDEA_CONVERT_TO_EPIC] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/convertToEpic`,
      "/project/${projectId}/v1/idea/${ideaId}/convertToEpic"
    )
  static readonly [EndpointTypes.IDEA_CONVERT_TO_FEATURE] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/convertToFeature`,
      "/project/${projectId}/v1/idea/${ideaId}/convertToFeature"
    )
  static readonly [EndpointTypes.IDEA_CONVERT_TO_ITEM] = (projectId: string, ideaId: string) =>
    new Url(
      `/project/${projectId}/v1/idea/${ideaId}/convertToItem`,
      "/project/${projectId}/v1/idea/${ideaId}/convertToItem"
    )
  // feature
  static readonly [EndpointTypes.SEARCH_FEATURE] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/feature/search`, "/account/${accountId}/v1/feature/search")
  static readonly [EndpointTypes.FEATURE] = (projectId: string) =>
    new Url(`/project/${projectId}/v2/feature`, "/project/${projectId}/v2/feature")
  static readonly [EndpointTypes.UPDATE_FEATURE] = (projectId: string, featureId: string) =>
    new Url(
      `/project/${projectId}/v2/feature/${featureId}`,
      "/project/${projectId}/v2/feature/${featureId}"
    )
  static readonly [EndpointTypes.DELETE_FEATURE] = (
    accountId: string,
    goalId: string,
    featureId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/goal/${goalId}/feature/${featureId}`,
      "/project/${projectId}/v2/feature/${featureId}"
    )
  static readonly [EndpointTypes.GET_FEATURE_EPICS] = (projectId: string, featureId: string) =>
    new Url(
      `/project/${projectId}/v2/feature/${featureId}/epic`,
      "/project/${projectId}/v2/feature/${featureId}/epic"
    )
  static readonly [EndpointTypes.UPDATE_FEATURE_EPIC] = (projectId: string, featureId: string) =>
    new Url(
      `/project/${projectId}/v2/feature/${featureId}/epic`,
      "/project/${projectId}/v2/feature/${featureId}/epic"
    )
  static readonly [EndpointTypes.ADD_PROJECT_FEATURE] = (
    projectId: string,
    featureId: string,
    toProjectId: string
  ) =>
    new Url(
      `/project/${projectId}/v2/feature/${featureId}/toProject/${toProjectId}`,
      "/project/${projectId}/v2/feature/${featureId}/toProject/${toProjectId}"
    )
  static readonly [EndpointTypes.CHANGE_FEATURE_PRIORITY] = (projectId: string) =>
    new Url(
      `/project/${projectId}/v2/feature/changePriority`,
      "/project/${projectId}/v2/feature/changePriority"
    )

  // email verification
  static readonly [EndpointTypes.EMAIL_VERIFICATION_SEND] = () =>
    new Url("/emailVerification/send", "/emailVerification/send")
  static readonly [EndpointTypes.EMAIL_VERIFICATION_RESEND] = () =>
    new Url("/emailVerification/resend", "/emailVerification/resend")

  // health check
  static readonly [EndpointTypes.SAVE_HEALTH_CHECK] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/healthCheck`,
      "/project/${projectId}/v1/sprint/${sprintId}/healthCheck"
    )
  static readonly [EndpointTypes.HEALTH_CHECK_SUMMARY] = (projectId: string, sprintId: string) =>
    new Url(
      `/project/${projectId}/v1/sprint/${sprintId}/healthCheck/summary`,
      "/project/${projectId}/v1/sprint/${sprintId}/healthCheck/summary"
    )

  // jira import
  static readonly [EndpointTypes.JIRA_IMPORT_START] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/jira-import`, "/account/${accountId}/v1/jira-import")
  static readonly [EndpointTypes.JIRA_IMPORT_USERS] = (accountId: string, importId: string) =>
    new Url(
      `/account/${accountId}/v1/jira-import/${importId}/users`,
      "/account/${accountId}/v1/jira-import/${importId}/users"
    )
  static readonly [EndpointTypes.JIRA_IMPORT_USERS_START] = (accountId: string, importId: string) =>
    new Url(
      `/account/${accountId}/v1/jira-import/${importId}/users/start`,
      "/account/${accountId}/v1/jira-import/${importId}/users/start"
    )
  static readonly [EndpointTypes.JIRA_IMPORT_BACKLOG_START] = (
    accountId: string,
    importId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jira-import/${importId}/import/start`,
      "/account/${accountId}/v1/jira-import/${importId}/import/start"
    )
  static readonly [EndpointTypes.JIRA_IMPORT_BACKLOG_FINISH] = (importId: string) =>
    new Url(`/api/internal/make/${importId}/finish`, "/api/internal/make/${importId}/finish")
  static readonly [EndpointTypes.JIRA_IMPORT_BACKLOG_SYNC_DISABLE] = (
    accountId: string,
    importId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jira-import/${importId}/sync/disable`,
      "/account/${accountId}/v1/jira-import/${importId}/sync/disable"
    )
  static readonly [EndpointTypes.JIRA_IMPORT_BACKLOG_SYNC_ENABLE] = (
    accountId: string,
    importId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jira-import/${importId}/sync/enable`,
      "/account/${accountId}/v1/jira-import/${importId}/sync/enable"
    )
  static readonly [EndpointTypes.JIRA_IMPORT_STATUS] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/jira-import/inProgress`,
      "/account/${accountId}/v1/jira-import/inProgress"
    )
  // jira sync
  static readonly [EndpointTypes.JIRA_SYNC_GET_BY_PROJECT] = (projectId: string) =>
    new Url(
      `/superadmin/jira-import/project/${projectId}`,
      `/superadmin/jira-import/project/${projectId}`
    )
  static readonly [EndpointTypes.JIRA_SYNC_CHANGE_PROJECT] = (projectId: string, enable: boolean) =>
    new Url(
      `/superadmin/jira-import/project/${projectId}?enable=${enable}`,
      `/superadmin/jira-import/project/${projectId}?enable=${enable}`
    )
  static readonly [EndpointTypes.JIRA_SYNC_CHANGE_PS_TO_JIRA] = (
    projectId: string,
    teamId: string,
    enable: boolean
  ) =>
    new Url(
      `/superadmin/jira-import/project/${projectId}/team/${teamId}/psToJira?enable=${enable}`,
      `/superadmin/jira-import/project/${projectId}/team/${teamId}/psToJira?enable=${enable}`
    )
  static readonly [EndpointTypes.JIRA_SYNC_CHANGE_JIRA_TO_PS] = (
    projectId: string,
    teamId: string,
    enable: boolean
  ) =>
    new Url(
      `/superadmin/jira-import/project/${projectId}/team/${teamId}/jiraToPs?enable=${enable}`,
      `/superadmin/jira-import/project/${projectId}/team/${teamId}/jiraToPs?enable=${enable}`
    )
  // jira mapping
  static readonly [EndpointTypes.JIRA_SETTING_GET_BY_TEAM] = (accountId: string, teamId: string) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/extended`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/extended"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_PS_TO_JIRA_STATUSES] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/statuses`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/statuses"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_PS_TO_JIRA_STATUS_DELETE] = (
    accountId: string,
    teamId: string,
    itemType: string,
    storyStatus: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/status/${itemType}/${storyStatus}`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/status/${itemType}/${storyStatus}"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_JIRA_TO_PS_STATUSES] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/statuses`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/statuses"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_JIRA_TO_PS_STATUS_DELETE] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/status`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/status"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPES] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/issueTypes`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/issueTypes"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPE_DELETE] = (
    accountId: string,
    teamId: string,
    itemType: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/issueTypes/${itemType}`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/issueTypes/${itemType}"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPES] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/issueTypes`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/issueTypes"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPE_DELETE] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/issueType`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/issueType"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/severityLevel`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/severityLevel"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL_DELETE] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/severityLevel`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/psToJira/severityLevel"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/severityLevel`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/severityLevel"
    )
  static readonly [EndpointTypes.JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL_DELETE] = (
    accountId: string,
    teamId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/severityLevel`,
      "/account/${accountId}/v1/jiraSetting/team/${teamId}/mapping/jiraToPs/severityLevel"
    )
  // timesheet
  static readonly [EndpointTypes.TIME_SHEET] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/timesheet`, `/account/${accountId}/v1/timesheet`)
  static readonly [EndpointTypes.TIME_SHEET_CURRENT] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/timesheet/current`,
      `/account/${accountId}/v1/timesheet/current`
    )
  static readonly [EndpointTypes.TIME_SHEET_SUMMARY] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/summary`,
      "/account/${accountId}/v2/timesheet/summary"
    )
  static readonly [EndpointTypes.TIME_SHEET_SEARCH] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/search`,
      "/account/${accountId}/v2/timesheet/search"
    )
  static readonly [EndpointTypes.TIME_SHEET_STATS] = (accountId: string) =>
    new Url(`/account/${accountId}/v2/timesheet/stats`, "/account/${accountId}/v2/timesheet/stats")
  static readonly [EndpointTypes.GET_TIME_SHEET] = (accountId: string, timesheetId: string) =>
    new Url(
      `/account/${accountId}/v1/timesheet/${timesheetId}`,
      "/account/${accountId}/v1/timesheet/${timesheetId}"
    )
  static readonly [EndpointTypes.TIME_SHEET_ENTRIES] = (accountId: string, timesheetId: string) =>
    new Url(
      `/account/${accountId}/v1/timesheet/${timesheetId}/entries`,
      "/account/${accountId}/v1/timesheet/${timesheetId}/entries"
    )
  static readonly [EndpointTypes.TIME_SHEET_SUBMIT] = (accountId: string, timesheetId: string) =>
    new Url(
      `/account/${accountId}/v1/timesheet/${timesheetId}/entries/submit`,
      "/account/${accountId}/v1/timesheet/${timesheetId}/entries/submit"
    )
  static readonly [EndpointTypes.TIME_SHEET_DECLINE] = (accountId: string, timesheetId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/${timesheetId}/decline`,
      "/account/${accountId}/v2/timesheet/${timesheetId}/decline"
    )
  static readonly [EndpointTypes.TIME_SHEET_APPROVE] = (accountId: string, timesheetId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/${timesheetId}/approve`,
      "/account/${accountId}/v2/timesheet/${timesheetId}/approve"
    )

  static readonly [EndpointTypes.TIME_SHEET_PERIOD_MONTH] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/periods/month`,
      "/account/${accountId}/v2/timesheet/periods/month"
    )

  static readonly [EndpointTypes.TIME_SHEET_PERIOD_WEEKS] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/periods/weeks`,
      "/account/${accountId}/v2/timesheet/periods/weeks"
    )

  static readonly [EndpointTypes.TIME_SHEET_PERIOD_AVAILABLE_WEEKS] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/timesheet/available/periods/weeks`,
      "/account/${accountId}/v1/timesheet/available/periods/weeks"
    )

  static readonly [EndpointTypes.TIME_SHEET_USERS_ENABLED] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/enabled/users`,
      "/account/${accountId}/v2/timesheet/enabled/users"
    )
  static readonly [EndpointTypes.GET_TIME_SHEET_SUMMARY_EXCEL] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/summary/excel`,
      "/account/${accountId}/v2/timesheet/summary/excel"
    )

  static readonly [EndpointTypes.TIME_SHEET_SUMMARY_WEEKS] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v2/timesheet/summary/weeks`,
      "/account/${accountId}/v2/timesheet/summary/weeks"
    )
  static readonly [EndpointTypes.TIME_SHEET_CHANGE_HISTORY] = (
    accountId: string,
    timesheetId: string
  ) =>
    new Url(
      `/account/${accountId}/v1/timesheet/${timesheetId}/history`,
      "/account/${accountId}/v1/timesheet/${timesheetId}/history"
    )
  static readonly [EndpointTypes.CALENDAR_EVENTS] = (accountId: string) =>
    new Url(`/account/${accountId}/v1/calendar-event`, "/account/${accountId}/v1/calendar-event")

  static readonly [EndpointTypes.CALENDAR_EVENT_BY_ID] = (accountId: string, eventId: string) =>
    new Url(
      `/account/${accountId}/v1/calendar-event/${eventId}`,
      "/account/${accountId}/v1/calendar-event/${eventId}"
    )
  static readonly [EndpointTypes.MANAGE_JIRA_USER_TOKEN] = (accountId: string, userId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/users/${userId}/token`,
      "/account/${accountId}/v1/admin/users/${userId}/token"
    )

  static readonly [EndpointTypes.SERVER_ERROR] = () =>
    new Url(`/superadmin/adhoc/testError`, "/superadmin/adhoc/testError")

  //stripe
  static readonly [EndpointTypes.UPGRADE_SUBSCRIPTION] = (accountId: string) =>
    new Url(
      `/account/${accountId}/v1/admin/payment/subscription`,
      "/account/${accountId}/v1/admin/payment/subscription"
    )

  static readonly [EndpointTypes.PROMO_CODE_CHECK] = () =>
    new Url(`/promoCode/check`, "/promoCode/check")
}
